// Japan geographic boundaries (restricts map panning to Japan)
export const JAPAN_BOUNDS = {
  north: 47, // Hokkaido northern tip
  south: 24.0, // Okinawa southern islands
  east: 149.0, // Eastern islands
  west: 127.0, // Western islands
}

// Zoom level limits
export const ZOOM_LIMITS = {
  desktop: { min: 5, max: 10, default: 5.8 },
  mobile: { min: 5, max: 10, default: 4.8 },
}

// Map configuration
export const MAP_CENTER = { lat: 36.2048, lng: 138.2529 } // Center of Japan

// Japanese airports data
export const japaneseAirports = [
  { name: '成田国際空港', nameEn: 'Narita International Airport', code: 'NRT', lat: 35.7697685, lng: 140.3772663 },
  {
    name: '東京国際空港',
    nameEn: 'Tokyo International Airport (Haneda)',
    code: 'HND',
    prefecture: '東京都',
    lat: 35.5476835,
    lng: 139.7684885,
  },
  {
    name: '関西国際空港',
    nameEn: 'Kansai International Airport',
    code: 'KIX',
    prefecture: '大阪府',
    lat: 34.435803,
    lng: 135.2350794,
  },
  {
    name: '中部国際空港',
    nameEn: 'Chubu Centrair International Airport',
    code: 'NGO',
    prefecture: '愛知県',
    lat: 34.857275,
    lng: 136.805869,
  },
  { name: '福岡空港', nameEn: 'Fukuoka Airport', code: 'FUK', lat: 33.5859, lng: 130.451 },
  { name: '新千歳空港', nameEn: 'New Chitose Airport', code: 'CTS', lat: 42.7752, lng: 141.692 },
  { name: '仙台空港', nameEn: 'Sendai Airport', code: 'SDJ', lat: 38.1397, lng: 140.917 },
  { name: '広島空港', nameEn: 'Hiroshima Airport', code: 'HIJ', lat: 34.4361, lng: 132.9194 },
  { name: '長崎空港', nameEn: 'Nagasaki Airport', code: 'NGS', lat: 32.9169, lng: 129.914 },
  { name: '松山空港', nameEn: 'Matsuyama Airport', code: 'MYJ', lat: 33.8272, lng: 132.6997 },
  { name: '函館空港', nameEn: 'Hakodate Airport', code: 'HKD', lat: 41.7757043, lng: 140.8132473 },
] as const

export type JapaneseAirport = (typeof japaneseAirports)[number]

// Airbnb-style map theme (soft, muted colors)
export const MAP_STYLES = [
  // Reset all geometry to soft green base
  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#e8f0e8' }] },

  // Water - soft light blue
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#aadaff' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6b' }] },

  // All landscape types - soft green
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#e8f0e8' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#f5f3f0' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#d4e8d4' }] },
  { featureType: 'landscape.natural.landcover', elementType: 'geometry', stylers: [{ color: '#d4e8d4' }] },
  { featureType: 'landscape.natural.terrain', elementType: 'geometry', stylers: [{ color: '#d4e8d4' }] },

  // Parks/Green areas - muted sage green (slightly darker)
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#c8e6c8' }] },
  { featureType: 'poi.sports_complex', elementType: 'geometry', stylers: [{ color: '#c8e6c8' }] },

  // Other POI - blend with background
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#e8f0e8' }] },
  { featureType: 'poi.business', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', elementType: 'geometry', stylers: [{ color: '#f5f3f0' }] },
  { featureType: 'poi.medical', elementType: 'geometry', stylers: [{ color: '#f5f3f0' }] },
  { featureType: 'poi.school', elementType: 'geometry', stylers: [{ color: '#f5f3f0' }] },

  // Roads - white with light gray stroke
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'road.local', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },

  // Labels - medium gray with white stroke
  { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6b' }] },
  { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 2 }] },

  // Administrative borders - light gray
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },

  // Transit - blend with background
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#e8f0e8' }] },
]

// Map options with zoom and drag enabled, restricted to Japan
export const getMapOptions = (isDesktop: boolean) => ({
  // Enable user interaction
  draggable: true,
  scrollwheel: true,
  gestureHandling: 'greedy',

  minZoom: isDesktop ? ZOOM_LIMITS.desktop.min : ZOOM_LIMITS.mobile.min,
  maxZoom: ZOOM_LIMITS.desktop.max,

  restriction: {
    latLngBounds: JAPAN_BOUNDS,
    strictBounds: false,
  },

  // Show zoom controls
  zoomControl: false,
  zoomControlOptions: {
    position: 4, // google.maps.ControlPosition.RIGHT_CENTER
  },

  // Keep these disabled
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  scaleControl: false,
  rotateControl: false,
  clickableIcons: false,
  disableDefaultUI: false,

  // Apply Airbnb-style theme
  styles: MAP_STYLES,
})
